import { computed, Injectable, Inject, inject, signal } from '@angular/core';
import {NotificationService} from './Notification.service';
import { HttpClient } from '@angular/common/http';
import { PureOriginalsModel } from '../models/PureOriginals-model';
import { SortDirectionValuesModel } from '../models/SortDirectionValues-model';
import { firstValueFrom, Subscription } from 'rxjs';

export enum VehicleDataFieldEnum {
    ECU_BUILD = 'ECU_BUILD',
    VEHICLE_PRODUCER = 'VEHICLE_PRODUCER',
    VEHICLE_TYPE = 'VEHICLE_TYPE',
    ECU_PRODUCER = 'ECU_PRODUCER',
}

export interface BatchUpdateResponse {
    pureOriginalIds: number[];
    message: string;
}

export interface PaginatedResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
}

export interface FaultyDataItem {
    value: string;
    count: number;
}

@Injectable({
    providedIn: 'root',
})
export class FaultyVehicleDataService {
    notificationService = inject(NotificationService);
    private url = `${this.API_URL}`;
    public faultyDataList = signal<FaultyDataItem[]>([]);
    public selectedField = signal<VehicleDataFieldEnum>(VehicleDataFieldEnum.ECU_BUILD);
    public pageNumber = signal<number>(0);
    public pageSize = signal<number>(50);
    public isLoading = signal<boolean>(false);
    public searchString = signal<string>('');
    public pureOriginals = signal<PureOriginalsModel[]>([]);

    public faultyPureOriginals = signal<PureOriginalsModel[]>([]);
    public pureOriginalsPageNumber = signal<number>(0);
    public pureOriginalsPageSize = signal<number>(50);
    public pureOriginalsIsLoading = signal<boolean>(false);
    public selectedFaultyValue = signal<string | null>(null);

    public displayedTableHeaders = signal<string[]>([]);
    public displayedOriginalColumns = signal<(keyof PureOriginalsModel)[]>([]);
    public sortDirectionValues = signal<SortDirectionValuesModel>({
        active: 'id',
        direction: 'desc',
    });

    readonly totalElements = signal(0);
    readonly totalPages = signal(0);
    readonly error = signal('');
    readonly pureOriginalsTotalPages = signal(0);
    readonly pureOriginalsError = signal('');
    readonly canLoadMore = computed(() => !this.isLoading() && !this.error() && this.pageNumber() + 1 < this.totalPages());
    readonly canLoadMoreOriginals = computed(() => this.selectedFaultyValue() !== null && !this.pureOriginalsIsLoading()
        && !this.pureOriginalsError() && this.pureOriginalsPageNumber() + 1 < this.pureOriginalsTotalPages());
    private listRequest?: Subscription;
    private originalsRequest?: Subscription;
    private requestedListPage = 0;
    private requestedListAppend = false;
    private requestedOriginalsPage = 0;
    private requestedOriginalsAppend = false;

    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {
    }

    getTableHeaderNamesForFaultyPureOriginals() {
        const pureOriginalRecord = new PureOriginalsModel();
        const objectKeys = Object.keys(pureOriginalRecord);

        this.displayedOriginalColumns.set([]);

        for (let key of objectKeys) {
            this.displayedOriginalColumns.update((columns) => [...columns, key as keyof PureOriginalsModel]);
        }

        const dataColumns = this.displayedOriginalColumns();
        this.displayedTableHeaders.set([...dataColumns, 'actions']);
    }

    reset() {
        this.searchFaultyData('');
    }

    resetToDefault() {
        this.selectedField.set(VehicleDataFieldEnum.ECU_BUILD);
        this.reset();
    }

    changeSortDirection(sortDirection: SortDirectionValuesModel) {
        this.sortDirectionValues.set(sortDirection);
        if (this.selectedFaultyValue() !== null) this.getFaultyPureOriginalsByValue(this.selectedFaultyValue());
    }

    searchFaultyData(searchTerm: string) {
        this.cancel();
        this.searchString.set(searchTerm.trim());
        this.pageNumber.set(0);
        this.totalElements.set(0);
        this.totalPages.set(0);
        this.faultyDataList.set([]);
        this.selectedFaultyValue.set(null);
        this.faultyPureOriginals.set([]);
        this.pureOriginalsPageNumber.set(0);
        this.pureOriginalsTotalPages.set(0);
        this.pureOriginalsError.set('');
        this.loadListPage(0);
    }

    retry(): void { this.loadListPage(this.requestedListPage, this.requestedListAppend); }

    onScroll(): void {
        if (this.canLoadMore()) this.loadListPage(this.pageNumber() + 1, true);
    }

    private loadListPage(pageNumber: number, append = false): void {
        this.listRequest?.unsubscribe();
        this.requestedListPage = pageNumber;
        this.requestedListAppend = append;
        this.error.set('');
        this.isLoading.set(true);
        const search = this.searchString();
        const params: Record<string, string | number> = {pageNumber, pageSize: this.pageSize()};
        if (search) Object.assign(params, {field: this.selectedField(), search, source: 'PURE_ORIGINAL_BASE'});
        else params['vehicleDataField'] = this.selectedField();
        this.listRequest = this.httpClient.get<PaginatedResponse<FaultyDataItem>>(
            `${this.url}/vehicle-data/${search ? 'search' : 'faulty-data'}`, {params}
        ).subscribe({
            next: response => {
                if (!Array.isArray(response?.content)) {
                    this.error.set('Could not load faulty values. Please try again.');
                } else {
                    this.faultyDataList.update(previous => [...new Map(
                        (append ? [...previous, ...response.content] : response.content).map(item => [item.value, item])
                    ).values()]);
                    this.pageNumber.set(pageNumber);
                    this.totalElements.set(response.totalElements);
                    this.totalPages.set(response.totalPages);
                }
                this.isLoading.set(false);
            },
            error: () => { this.error.set('Could not load faulty values. Please try again.'); this.isLoading.set(false); }
        });
    }

    getFaultyPureOriginalsByValue(faultyValue: string) {
        this.originalsRequest?.unsubscribe();
        this.selectedFaultyValue.set(faultyValue);
        this.pureOriginalsPageNumber.set(0);
        this.pureOriginalsTotalPages.set(0);
        this.faultyPureOriginals.set([]);
        this.loadOriginalsPage(0);
    }

    retryPureOriginals(): void { this.loadOriginalsPage(this.requestedOriginalsPage, this.requestedOriginalsAppend); }

    onPureOriginalsScroll(): void {
        if (this.canLoadMoreOriginals()) this.loadOriginalsPage(this.pureOriginalsPageNumber() + 1, true);
    }

    private loadOriginalsPage(pageNumber: number, append = false): void {
        this.originalsRequest?.unsubscribe();
        this.requestedOriginalsPage = pageNumber;
        this.requestedOriginalsAppend = append;
        this.pureOriginalsError.set('');
        this.pureOriginalsIsLoading.set(true);
        this.originalsRequest = this.httpClient.get<PaginatedResponse<PureOriginalsModel>>(
            `${this.url}/vehicle-data/faulty-data/pure-originals`, {
                params: {field: this.selectedField(), value: this.selectedFaultyValue(), pageNumber,
                    pageSize: this.pureOriginalsPageSize(), sortAttribute: this.sortDirectionValues().active,
                    sortDirection: this.sortDirectionValues().direction}
            }
        ).subscribe({
            next: response => {
                if (!Array.isArray(response?.content)) {
                    this.pureOriginalsError.set('Could not load Pure Originals. Please try again.');
                } else {
                    this.faultyPureOriginals.update(previous => [...new Map(
                        (append ? [...previous, ...response.content] : response.content).map(item => [String(item.id), item])
                    ).values()]);
                    this.pureOriginalsPageNumber.set(pageNumber);
                    this.pureOriginalsTotalPages.set(response.totalPages);
                }
                this.pureOriginalsIsLoading.set(false);
            },
            error: () => { this.pureOriginalsError.set('Could not load Pure Originals. Please try again.'); this.pureOriginalsIsLoading.set(false); }
        });
    }

    cancel(): void {
        this.listRequest?.unsubscribe();
        this.originalsRequest?.unsubscribe();
        this.isLoading.set(false);
        this.pureOriginalsIsLoading.set(false);
    }

    async updateFaultyPureOriginals(pureOriginalIds: number[], newValue: string): Promise<BatchUpdateResponse> {
        return await firstValueFrom(
            this.httpClient.put<BatchUpdateResponse>(
                `${this.url}/vehicle-data/faulty-data/pure-originals/update`,
                pureOriginalIds,
                {
                    params: {
                        field: this.selectedField(),
                        newValue: newValue,
                    },
                }
            )
        );
    }

    changeSelectedField(field: VehicleDataFieldEnum) {
        this.selectedField.set(field);
        this.reset();
    }

    getVehicleTypes() {
        return this.httpClient.get<string[]>(`${this.API_URL}/vehicle-types/correct`);
    }

    getVehicleProducers() {
        return this.httpClient.get<string[]>(`${this.API_URL}/vehicle-producer/correct`);
    }
}
